import React from 'react';
import { Box, Typography } from '@mui/material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { useTranslation } from 'react-i18next';

import type { ScriptStep } from '@shared/interfaces/assistantScripts';

import { estimateStepSeconds, formatSeconds, hasQuiz } from './scriptData';
import { useAiInspectorStyles } from './inspectorStyles';

const QUIZ_LABEL_KEY: Record<
  'multiple_choice' | 'text_input' | 'none',
  { key: string }
> = {
  multiple_choice: { key: 'editorMode.aiQuizMultiple' },
  text_input: { key: 'editorMode.aiQuizTextInput' },
  none: { key: 'editorMode.aiQuizNone' },
};

interface IScriptTimelineProps {
  steps: ScriptStep[];
}

const ScriptTimeline = ({ steps }: IScriptTimelineProps) => {
  const { t: translate } = useTranslation();
  const classes = useAiInspectorStyles();

  if (steps.length === 0) {
    return (
      <Typography sx={classes.emptyHint}>
        {translate('editorMode.aiTimelineEmpty')}
      </Typography>
    );
  }

  return (
    <Box sx={classes.timeline} role="list">
      {steps.map((step, index) => {
        const text =
          typeof step.text === 'string' && step.text.trim().length > 0
            ? step.text
            : translate('editorMode.aiStepEmpty');
        const seconds = estimateStepSeconds(step.text ?? '');
        const quizType = step.questionary?.type ?? 'none';
        const quizLabel = QUIZ_LABEL_KEY[quizType];

        return (
          <Box
            key={`step-${index}`}
            sx={classes.step}
            role="listitem"
            aria-label={translate('editorMode.aiStepN', {
              n: index + 1,
            })}
          >
            <Typography sx={classes.stepText}>{text}</Typography>
            <Box sx={classes.stepMeta}>
              <AccessTimeOutlinedIcon sx={{ fontSize: 10 }} />~
              {formatSeconds(seconds)}
              {hasQuiz(step) ? (
                <Box component="span" sx={classes.stepQuiz}>
                  <AutoAwesomeOutlinedIcon sx={{ fontSize: 10 }} />
                  {translate(quizLabel.key)}
                </Box>
              ) : null}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ScriptTimeline;
